/**
 * Fair Home V2 — the section pager (it replaced Swiper).
 *
 * Same look as the Swiper pager: the next section slides up from below over the
 * current one, which stays put; paging back slides the current one down.
 * `revealLast` (the footer, T-150): the last section lies still under the one before
 * it, and that one slides up and away to uncover it — the page "leaves" the footer.
 *
 * What it does differently: a transition can be interrupted. A swipe made while
 * the pager is still moving re-aims it at the next section from where it is, the
 * way iOS lists do — Swiper drops every wheel event during its transition, so a
 * quick next swipe was lost.
 *
 * Input:
 *  - wheel: one page per swipe. `createFairHomeV2WheelGate` (lethargy) keeps a
 *    trackpad's inertial tail out; a swipe starts with the first hand event after
 *    inertia, a pause or a turn. Horizontal wheel is left to the carousels.
 *  - touch: the section follows the finger; on release it pages on distance or
 *    speed, else settles back. Horizontal drags are left to the carousels.
 *  - keys: ↓ / PageDown / Space next, ↑ / PageUp / Shift+Space back, Home, End.
 */
import { onBeforeUnmount, onMounted, ref, type Ref } from 'vue'
import { createFairHomeV2WheelGate } from './useFairHomeV2WheelGate'

/** A hand never swipes twice this fast: a stream that dips and rises again is one swipe. */
const MIN_SWIPE_INTERVAL_MS = 200

/** A pause between wheel events this long ends a swipe (lethargy's own delay). */
const SWIPE_GAP_MS = 100

/** Touch: a drag this far (share of the screen) pages on release. */
const TOUCH_PAGE_DISTANCE = 0.2

/** Touch: a release this fast (px/ms) pages whatever the distance. */
const TOUCH_PAGE_VELOCITY = 0.3

/** Touch: movement before the drag picks an axis. */
const TOUCH_AXIS_LOCK_PX = 8

/** Touch: pulling past the first or the last section moves this much of the finger. */
const EDGE_RESISTANCE = 0.25

const SLIDE_SELECTOR = ':scope > .fair-home-v2__slide'

/** CSS `ease` — what Swiper's transition used. */
function ease(t: number): number {
  // cubic-bezier(0.25, 0.1, 0.25, 1), solved for x by Newton's method.
  const x1 = 0.25
  const y1 = 0.1
  const x2 = 0.25
  const y2 = 1
  const bx = (u: number) => 3 * x1 * u * (1 - u) ** 2 + 3 * x2 * u ** 2 * (1 - u) + u ** 3
  const by = (u: number) => 3 * y1 * u * (1 - u) ** 2 + 3 * y2 * u ** 2 * (1 - u) + u ** 3
  const dx = (u: number) =>
    3 * x1 * (1 - u) ** 2 + 6 * (x2 - x1) * u * (1 - u) + 3 * (1 - x2) * u ** 2
  let u = t
  for (let i = 0; i < 6; i += 1) {
    const d = dx(u)
    if (Math.abs(d) < 1e-6) break
    u -= (bx(u) - t) / d
  }
  return by(Math.min(1, Math.max(0, u)))
}

/** Re-aimed mid-flight: no slow start, the motion carries on. */
function easeOut(t: number): number {
  return 1 - (1 - t) ** 3
}

function isEditable(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null
  if (!el?.closest) return false
  return Boolean(el.closest('input, textarea, select, [contenteditable=""], [contenteditable="true"]'))
}

function isPressable(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null
  if (!el?.closest) return false
  return Boolean(el.closest('button, a[href], [role="button"], summary'))
}

export interface FairHomeV2OwnPagerOptions {
  count: number
  speedMs: number
  /** Called when the pager sets off for another section (as Swiper's `slideChange`). */
  onChange: (index: number, previous: number) => void
  /** T-150 — the last section is uncovered (the one before slides up), not slid in over. */
  revealLast?: boolean
}

export function useFairHomeV2OwnPager(
  pagerRef: Ref<HTMLElement | null>,
  { count, speedMs, onChange, revealLast = false }: FairHomeV2OwnPagerOptions,
) {
  const activeIndex = ref(0)
  /** Continuous position: 2.4 = section 2 with section 3 a 40% way up over it. */
  let position = 0
  let frame = 0
  let slides: HTMLElement[] = []

  function render() {
    const base = Math.floor(position)
    const last = slides.length - 1
    slides.forEach((slide, i) => {
      let offset = Math.min(1, Math.max(0, i - position))
      if (revealLast && i === last) offset = 0
      else if (revealLast && i === last - 1 && position > i) offset = -Math.min(1, position - i)
      slide.style.transform = `translate3d(0, ${(offset * 100).toFixed(4)}%, 0)`
      // The uncovered last section lies under the rest (DOM order would put it on top).
      if (revealLast) slide.style.zIndex = i === last ? '0' : '1'
      // Covered or off-screen sections take no paint and no focus.
      const visible = i === base || i === base + 1
      slide.style.visibility = visible ? '' : 'hidden'
      slide.toggleAttribute('inert', i !== activeIndex.value)
    })
  }

  function stopAnimation() {
    if (frame) cancelAnimationFrame(frame)
    frame = 0
  }

  function animateTo(target: number) {
    const from = position
    const moving = frame !== 0
    stopAnimation()
    if (from === target) return
    const curve = moving ? easeOut : ease
    // A re-aim mid-flight covers less ground — it takes proportionally less time.
    const duration = speedMs * Math.min(1, Math.abs(target - from))
    const start = performance.now()
    const step = (now: number) => {
      const t = Math.min(1, (now - start) / duration)
      position = from + (target - from) * curve(t)
      render()
      frame = t < 1 ? requestAnimationFrame(step) : 0
    }
    frame = requestAnimationFrame(step)
  }

  function goTo(index: number) {
    const next = Math.min(count - 1, Math.max(0, index))
    const previous = activeIndex.value
    if (next !== previous) {
      activeIndex.value = next
      onChange(next, previous)
    }
    animateTo(next)
  }

  // —— wheel ——
  const readWheel = createFairHomeV2WheelGate()
  let inSwipe = false
  let lastWheelAt = -Infinity
  let lastDirection = 0
  let swipeStartedAt = -Infinity

  function onWheel(event: WheelEvent) {
    if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) return
    const passes = readWheel(event)
    const direction = Math.sign(event.deltaY)
    const reversed = direction !== 0 && lastDirection !== 0 && direction !== lastDirection

    // Inertia, a pause or a turn ends the swipe: the next hand event starts a new one.
    if (!passes || reversed || event.timeStamp - lastWheelAt > SWIPE_GAP_MS) inSwipe = false
    lastWheelAt = event.timeStamp
    if (!passes || direction === 0) return
    lastDirection = direction

    if (inSwipe) return
    if (!reversed && event.timeStamp - swipeStartedAt < MIN_SWIPE_INTERVAL_MS) return
    inSwipe = true
    swipeStartedAt = event.timeStamp
    goTo(activeIndex.value + direction)
  }

  // —— touch ——
  let touchId: number | null = null
  let touchStartX = 0
  let touchStartY = 0
  let touchStartPosition = 0
  let touchAxis: 'x' | 'y' | null = null
  /** Recent samples for the release speed. */
  let touchSamples: Array<{ y: number; t: number }> = []

  function onTouchStart(event: TouchEvent) {
    if (touchId !== null || event.touches.length !== 1) return
    const touch = event.touches[0]
    touchId = touch.identifier
    touchStartX = touch.clientX
    touchStartY = touch.clientY
    touchAxis = null
    touchSamples = [{ y: touch.clientY, t: event.timeStamp }]
  }

  function onTouchMove(event: TouchEvent) {
    const touch = Array.from(event.changedTouches).find((t) => t.identifier === touchId)
    if (!touch) return
    const dx = touch.clientX - touchStartX
    const dy = touch.clientY - touchStartY

    if (touchAxis === null) {
      if (Math.hypot(dx, dy) < TOUCH_AXIS_LOCK_PX) return
      touchAxis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y'
      if (touchAxis === 'y') {
        // Caught mid-flight: the drag starts from where the pager is.
        stopAnimation()
        touchStartPosition = position
        touchStartY = touch.clientY
      }
    }
    if (touchAxis !== 'y') return

    event.preventDefault()
    const height = pagerRef.value?.clientHeight || window.innerHeight
    let next = touchStartPosition - (touch.clientY - touchStartY) / height
    if (next < 0) next *= EDGE_RESISTANCE
    if (next > count - 1) next = count - 1 + (next - (count - 1)) * EDGE_RESISTANCE
    position = next
    render()

    touchSamples.push({ y: touch.clientY, t: event.timeStamp })
    if (touchSamples.length > 6) touchSamples.shift()
  }

  function onTouchEnd(event: TouchEvent) {
    const ended = Array.from(event.changedTouches).some((t) => t.identifier === touchId)
    if (!ended) return
    touchId = null
    if (touchAxis !== 'y') return

    const first = touchSamples[0]
    const last = touchSamples[touchSamples.length - 1]
    const velocity = last.t > first.t ? (last.y - first.y) / (last.t - first.t) : 0
    const travelled = position - activeIndex.value

    let target = activeIndex.value
    if (velocity <= -TOUCH_PAGE_VELOCITY || travelled >= TOUCH_PAGE_DISTANCE) target += 1
    else if (velocity >= TOUCH_PAGE_VELOCITY || travelled <= -TOUCH_PAGE_DISTANCE) target -= 1
    goTo(target)
  }

  // —— keys ——
  function onKeydown(event: KeyboardEvent) {
    if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return
    if (isEditable(event.target)) return
    // A drawer or a dialog over the home owns its keys.
    if ((event.target as HTMLElement | null)?.closest?.('[role="dialog"], [aria-modal="true"]')) return
    let target: number | null = null
    switch (event.key) {
      case 'ArrowDown':
      case 'PageDown':
        target = activeIndex.value + 1
        break
      case 'ArrowUp':
      case 'PageUp':
        target = activeIndex.value - 1
        break
      case ' ':
        if (isPressable(event.target)) return
        target = activeIndex.value + (event.shiftKey ? -1 : 1)
        break
      case 'Home':
        target = 0
        break
      case 'End':
        target = count - 1
        break
      default:
        return
    }
    event.preventDefault()
    goTo(target)
  }

  onMounted(() => {
    const pager = pagerRef.value
    if (!pager) return
    slides = Array.from(pager.querySelectorAll<HTMLElement>(SLIDE_SELECTOR))
    render()
    pager.addEventListener('wheel', onWheel, { passive: true })
    pager.addEventListener('touchstart', onTouchStart, { passive: true })
    pager.addEventListener('touchmove', onTouchMove, { passive: false })
    pager.addEventListener('touchend', onTouchEnd, { passive: true })
    pager.addEventListener('touchcancel', onTouchEnd, { passive: true })
    window.addEventListener('keydown', onKeydown)
  })

  onBeforeUnmount(() => {
    stopAnimation()
    const pager = pagerRef.value
    pager?.removeEventListener('wheel', onWheel)
    pager?.removeEventListener('touchstart', onTouchStart)
    pager?.removeEventListener('touchmove', onTouchMove)
    pager?.removeEventListener('touchend', onTouchEnd)
    pager?.removeEventListener('touchcancel', onTouchEnd)
    window.removeEventListener('keydown', onKeydown)
  })

  return { activeIndex, goTo }
}
